/**
 * Recipe video fullscreen (Made in Matcha).
 *
 * Takes a recipe video fullscreen the moment it starts playing, on the card
 * (`snippets/custom.recipe-card.liquid`) and on the recipe page
 * (`sections/custom-recipe-page.liquid`). These videos carry a text overlay baked into the
 * footage, so a card-sized player is the wrong place to actually watch one — the card is the
 * poster, fullscreen is the viewing.
 *
 * ONE DOCUMENT LISTENER, IN CAPTURE PHASE. `play` does not bubble, so a delegated listener only
 * sees it with `capture: true`. That is the reason for the third argument, and removing it would
 * silently stop this working rather than throw. Delegating rather than binding per <video> means
 * cards rendered after load — the carousel is a plain scroll row today, but a future paginated or
 * filtered version would add cards — are covered without a re-scan.
 *
 * WHY `play` AND NOT A CLICK HANDLER. The videos use native controls, and a click on the browser's
 * own play button is not distinguishable from a click anywhere else on the element: the control is
 * inside the shadow DOM and reports the <video> as its target. `play` is the only reliable signal
 * that playback actually started.
 *
 * The cost of that choice is user activation. Fullscreen needs transient activation, and `play`
 * fires a beat after the click that caused it. In practice the activation survives — the event is
 * dispatched in the same task as the gesture — but it is not guaranteed, and a rejected request is
 * a normal outcome rather than an error: the video keeps playing inline, which is exactly what it
 * did before this file existed. So every failure path here is swallowed deliberately.
 *
 * iOS Safari is the reason for the second branch. It does not implement the Element fullscreen API
 * on <video> at all; it has `webkitEnterFullscreen` on the element instead, which is also the only
 * way to escape the `playsinline` attribute the cards set. Feature-detected, not UA-sniffed.
 *
 * Loaded from both surfaces that render a recipe video. A module is evaluated once per URL no
 * matter how many tags reference it, so the duplicate <script> costs nothing.
 */

/** Selectors for the two places a recipe video renders. Keep in step with the two Liquid files. */
const VIDEO_SELECTOR = '.custom-recipe-card__media-el, .custom-recipe-page__media-el';

/**
 * @param {HTMLVideoElement} video
 */
function enterFullscreen(video) {
  // Something is already fullscreen — most likely this video, if `play` fired again after a seek
  // or a pause/resume. Re-requesting would be a no-op at best and a flicker at worst.
  if (document.fullscreenElement || /** @type {any} */ (document).webkitFullscreenElement) return;

  const request = video.requestFullscreen || /** @type {any} */ (video).webkitRequestFullscreen;

  if (typeof request === 'function') {
    try {
      const result = request.call(video);
      // Standard API returns a promise; the prefixed one predates promises and returns undefined.
      if (result && typeof result.catch === 'function') result.catch(() => {});
    } catch {
      // Denied for want of user activation, or blocked by permissions policy. Play inline.
    }
    return;
  }

  // iOS Safari: no Element fullscreen on <video>, only this. Throws if metadata has not loaded
  // yet, which `preload="metadata"` makes unlikely but not impossible on a cold cache.
  const iosFullscreen = /** @type {any} */ (video).webkitEnterFullscreen;
  if (typeof iosFullscreen === 'function') {
    try {
      iosFullscreen.call(video);
    } catch {
      // Same deal — inline playback is the fallback.
    }
  }
}

document.addEventListener(
  'play',
  (event) => {
    const target = event.target;
    if (!(target instanceof HTMLVideoElement)) return;
    if (!target.matches(VIDEO_SELECTOR)) return;
    enterFullscreen(target);
  },
  true
);
